import { isStartAndEndTimeValid, rruleEndsBeforeStart, } from './validationUtils';
// https://support.nylas.com/hc/en-us/articles/10571467644957-Event-Description-Limits-in-Google-Calendar-and-Microsoft-Exchange
// In mysql it's a medium text, 16mb limit
// In PHP there's a 100000 char limit using https://www.php.net/manual/en/function.mb-strlen.php not sure of encoding
// so 100000 unicode code points is certainly safe, 32k probably safe too, lower limits risk breakage
export var MAX_DESCRIPTION_LENGTH = 32000;
/**
 * External, non-form blockers. There is exactly one today, so this is a
 * plain predicate — deliberately not generalized from a sample size of one.
 *
 * @TODO Future improvement (when a second alert exists): derive BlockingAlertId
 * from a per-alert inputs map (`BlockingAlertId = keyof BlockingAlertInputs`)
 * and key a rules Record here + a presentation registry in
 * ComposerBlockingAlert off it, so each alert's types stay independent and
 * the compiler forces full wiring.
 */
export function resolveBlockingAlerts(input) {
    /* This is very specific to calendar integrations, so we need to check if the user has a calendar integration */
    var personalCalendarRequired = input.isCreateMode &&
        input.isSpaceRequestOnlyEvent &&
        !input.isLoadingCalendarIntegration &&
        !input.canCreateRequestWithPersonalCalendar;
    return personalCalendarRequired
        ? [
            {
                kind: 'blocking-alert',
                id: 'personal-calendar-required',
                target: 'blocking-alert',
            },
        ]
        : [];
}
/**
 * A title is mandatory only for request-only events. Shared by the resolver
 * and TitleInput's inline help so display and submit logic cannot disagree.
 */
export function isTitleMissing(input) {
    var _a;
    return input.isSpaceRequestOnlyEvent && !((_a = input.title) === null || _a === void 0 ? void 0 : _a.trim());
}
/**
 * Field-level blockers, ordered top -> bottom of the composer so the FIRST
 * entry is the natural scroll target. If composer layout order changes,
 * update this function's ordering to match.
 */
export function resolveFieldIssues(input) {
    var _a, _b;
    var issues = [];
    // 1. Title (request-only)
    if (isTitleMissing(input)) {
        issues.push({ kind: 'field', target: 'title', id: 'title-required' });
    }
    // 2. Date & time
    if (!isStartAndEndTimeValid(input.startTime, input.endTime, input.now, input.isCreateMode)) {
        issues.push({
            kind: 'field',
            target: 'datetime',
            id: 'invalid-event-time',
        });
    }
    if (rruleEndsBeforeStart(input.recurrenceRule, input.date)) {
        issues.push({
            kind: 'field',
            target: 'datetime',
            id: 'recurrence-ends-before-start',
        });
    }
    // 3. Spaces
    // EventComposerGlobalContext.updateSpaceConflicts only stores entries that
    // actually conflict and prunes the map to null when none remain, so plain
    // truthiness is exact — do NOT re-derive by iterating entries.
    if (input.spacesWithConflicts) {
        issues.push({ kind: 'field', target: 'spaces', id: 'space-conflict' });
    }
    if (input.conflictingSpaceIds.length > 0) {
        issues.push({ kind: 'field', target: 'spaces', id: 'abt-conflict' });
    }
    if (input.hasMaxLengthViolation) {
        issues.push({ kind: 'field', target: 'spaces', id: 'max-length' });
    }
    if (input.requestFormId && !input.isSpaceRequestFormCompleted) {
        issues.push({
            kind: 'field',
            target: 'spaces',
            id: 'incomplete-request-form',
        });
    }
    // 4. Description — watched value only; the inline error in Description.tsx
    // derives from the same value and constant, so display and validation agree.
    if (((_b = (_a = input.description) === null || _a === void 0 ? void 0 : _a.length) !== null && _b !== void 0 ? _b : 0) > MAX_DESCRIPTION_LENGTH) {
        issues.push({
            kind: 'field',
            target: 'description',
            id: 'description-too-long',
        });
    }
    return issues;
}
export function resolveAllIssues(input) {
    return {
        blockingAlerts: resolveBlockingAlerts(input),
        fieldIssues: resolveFieldIssues(input),
    };
}
